'use client';

import { useQuery } from '@tanstack/react-query';
import { RotateCcw } from 'lucide-react';
import { getListingScores } from '@/lib/api';
import { Skeleton } from '@/components/ui/Skeleton';

interface Props {
  listingId: string;
}

function scoreTone(score: number): string {
  if (score >= 70) return 'text-emerald-300';
  if (score >= 50) return 'text-gold-300';
  return 'text-coral-300';
}

type ScoreKind = 'walk' | 'transit' | 'bike';

// Walk Score's own published bands, so a bare number reads plainly (a 0 transit
// score is "Minimal transit", not a value that looks like a load error).
const SCORE_WORDS: Record<ScoreKind, [string, string, string, string, string]> = {
  walk: ['Car-dependent', 'Car-dependent', 'Somewhat walkable', 'Very walkable', "Walker's paradise"],
  transit: ['Minimal transit', 'Some transit', 'Good transit', 'Excellent transit', "Rider's paradise"],
  bike: ['Somewhat bikeable', 'Somewhat bikeable', 'Bikeable', 'Very bikeable', "Biker's paradise"],
};

function scoreDescriptor(kind: ScoreKind, v: number): string {
  const band = v >= 90 ? 4 : v >= 70 ? 3 : v >= 50 ? 2 : v >= 25 ? 1 : 0;
  return SCORE_WORDS[kind][band];
}

function ratingTone(rating: number): string {
  if (rating >= 8) return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30';
  if (rating >= 5) return 'bg-gold-500/15 text-gold-300 border-gold-500/30';
  return 'bg-coral-500/15 text-coral-300 border-coral-500/30';
}

interface ScoresPayload {
  scores?: { walk?: number; transit?: number; bike?: number } | null;
  schools?: Array<{
    name: string;
    rating: number;
    distance: number;
    grade_range?: string;
    type?: string;
  }> | null;
}

function ScoresSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-7 w-44" />
      <div className="grid grid-cols-3 gap-4">
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
    </div>
  );
}

/**
 * Walk/Transit/Bike scores (Walk Score, live) + any nearby schools.
 *
 * A client island so the three secondary-data states are honest and recoverable:
 * skeleton while loading, the real scores on success, a *retryable* "couldn't
 * load" on failure (never a raw error or an infinite spinner), and clean absence
 * when the API has nothing to show. React Query retries transient failures on its
 * own; the manual "Try again" covers a persistent blip so the real Walk Score
 * data is never silently swallowed. Schools stay absent unless the feed has them.
 */
export default function ScoresSection({ listingId }: Props) {
  const { data, isPending, isError, refetch, isFetching } = useQuery({
    queryKey: ['listing-scores', listingId],
    queryFn: () => getListingScores(listingId) as Promise<ScoresPayload>,
    retry: 2,
    staleTime: 24 * 60 * 60 * 1000,
  });

  if (isPending) {
    return <ScoresSkeleton />;
  }

  if (isError) {
    return (
      <section>
        <h2 className="font-display mb-4 text-2xl text-ink">Location scores</h2>
        <div className="glass flex flex-col items-start gap-3 rounded-card p-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-ink-muted">
            We couldn&apos;t load walk, transit &amp; bike scores just now.
          </p>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-hairline bg-glass px-3 py-1.5 text-sm font-medium text-emerald-300 transition-colors hover:bg-glass-bright disabled:opacity-60"
          >
            <RotateCcw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            {isFetching ? 'Loading…' : 'Try again'}
          </button>
        </div>
      </section>
    );
  }

  const scoreItems = data?.scores
    ? ([
        // Consistent one-word labels (the heading + attribution carry "score");
        // avoids "Walk Score" wrapping to two lines in the narrow mobile column.
        { kind: 'walk', label: 'Walk', value: data.scores.walk },
        { kind: 'transit', label: 'Transit', value: data.scores.transit },
        { kind: 'bike', label: 'Bike', value: data.scores.bike },
      ].filter((s) => typeof s.value === 'number') as {
        kind: ScoreKind;
        label: string;
        value: number;
      }[])
    : [];

  const schools = data?.schools ?? [];

  // Clean absence — nothing real to show (never a broken/empty widget).
  if (scoreItems.length === 0 && schools.length === 0) {
    return null;
  }

  return (
    <section className="space-y-8">
      {scoreItems.length > 0 && (
        <div>
          <h2 className="font-display mb-4 text-2xl text-ink">Location scores</h2>
          <div className="grid grid-cols-3 gap-4">
            {scoreItems.map((s) => (
              <div key={s.label} className="glass flex flex-col items-center rounded-card p-4 text-center">
                <span className={`font-display text-3xl tabular-nums ${scoreTone(s.value)}`}>
                  {s.value}
                </span>
                <span className="mt-1 text-sm font-medium text-ink-soft">{s.label}</span>
                <span className="mt-0.5 text-xs text-ink-muted">
                  {scoreDescriptor(s.kind, s.value)}
                </span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-ink-faint">
            Walk, Transit &amp; Bike Scores by{' '}
            <a
              href="https://www.walkscore.com"
              target="_blank"
              rel="noopener noreferrer"
              className="underline transition-colors hover:text-ink-muted"
            >
              Walk Score®
            </a>
          </p>
        </div>
      )}

      {schools.length > 0 && (
        <div>
          <h2 className="font-display mb-4 text-2xl text-ink">Nearby schools</h2>
          <ul className="space-y-2">
            {schools.map((school, idx) => (
              <li key={idx} className="glass flex items-center justify-between rounded-xl p-4">
                <div>
                  <p className="font-medium text-ink">{school.name}</p>
                  <p className="text-sm text-ink-muted">
                    {[school.grade_range, school.type, `${school.distance.toFixed(1)} mi`]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <span
                  className={`flex h-10 w-10 items-center justify-center rounded-full border font-bold tabular-nums ${ratingTone(school.rating)}`}
                >
                  {school.rating}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-ink-faint">School ratings via GreatSchools</p>
        </div>
      )}
    </section>
  );
}
